import { clientPosToWorldPos } from '@/editor/utils';
import { EEntityType } from '@/editor/types';
import { globalStores, TGlobalStores } from '@/editor/stores';
import { CommonController2d } from '../CommonController';
import FurnitureEntity from '@/editor/entities/furnitureEntity';
import { MULTI_SELECT_TEMPORARY_ID } from '@/editor/constant/entity';
import { RulerModel } from '@/editor/entities/2d/rulerModel';
import { calculateBoxRulerInfos } from '@/editor/stores/utils/ruler/model';
import { MultiModelAbsorbManager } from '@/editor/manager/absorb/box/multiModelAbsorbManager';
import { cloneDeep } from 'lodash-es';
import { MULTI_ID_SUFFIX } from '@/editor/controller/helper/constants';
import { OperateSelectedBoxes } from '@/editor/controller/helper/operateSelectedBoxes';
import { IMultiModelData } from '@/editor/data/multiModel/multiModel';
import { adsorbModelInfos } from '@/editor/data/multiModel/utils';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { getBoxRayDetectOutlines } from '@/editor/stores/utils/ruler/outlines';

export class DrawMultiModel extends CommonController2d {
  private _checkedData: IMultiModelData | null = null;
  private _virtualData: IMultiModelData | null = null;

  private _virtualEntityMap: Map<string, FurnitureEntity[]> = new Map();
  private _rulerModel = new RulerModel();
  private _rayDetectOutlines: { start: IPoint2d; end: IPoint2d }[] = [];

  private _multiModelAbsorbManager = new MultiModelAbsorbManager();

  private _selectedBoxes = new OperateSelectedBoxes();

  constructor(_gs: TGlobalStores) {
    super(_gs);

    const virtualMultiModelData = globalStores.editorStateStore.virtualMultiModelData;

    if (!virtualMultiModelData) return;

    this._checkedData = virtualMultiModelData;
    this._virtualData = cloneDeep(virtualMultiModelData);

    this._multiModelAbsorbManager.updateData();
    this._updateRayDetectOutlines();
    this.updateBoxes();
  }

  private _updateRayDetectOutlines() {
    this._rayDetectOutlines = getBoxRayDetectOutlines();
  }

  protected override mouseMove = (e: MouseEvent) => {
    if (!this._checkedData) return;

    const {
      renderer,
      scene2d: { camera }
    } = this._gs.editorStateStore.sceneInfo;
    const mousePosition = clientPosToWorldPos(e, camera, renderer);

    const originMultiModelBox = { ...this._checkedData };
    const originModelInfos = this._checkedData.modelInfos;
    const absorbBox = { ...originMultiModelBox, center: mousePosition };
    const absorbInfo = this._multiModelAbsorbManager.calculateThenRender(absorbBox);
    if (absorbInfo) {
      absorbBox.center = absorbInfo.absorbPoint;
      absorbBox.rotation = absorbInfo.rotation;
    }

    const moveModelInfos = adsorbModelInfos(originModelInfos, absorbBox, originMultiModelBox);
    this._virtualData = { ...this._checkedData, modelInfos: moveModelInfos };

    this.updateVirtualEntity();

    this.updateBoxes({ x: absorbBox.center.x, y: absorbBox.center.y, rotation: absorbBox.rotation });

    const rulerInfos = calculateBoxRulerInfos(absorbBox, this._rayDetectOutlines);
    this._rulerModel.updateModel(rulerInfos);
  };

  protected override mouseUp = (e: MouseEvent, isDragged: boolean) => {
    if (isDragged) return;

    if (e.button === 2 || !this._virtualData) {
      this.dispose();
      this.exit();
      return;
    }

    if (e.button !== 0) return;

    this._gs.multiModelOperation.copyMultiModelData(this._virtualData.modelInfos);
    this._multiModelAbsorbManager.clearModel();
    this._multiModelAbsorbManager.updateData();
    this._updateRayDetectOutlines();
  };

  private updateVirtualEntity() {
    if (!this._virtualData) return;

    const modelInfos = this._virtualData.modelInfos;
    const len = modelInfos.length;

    for (let i = 0; i < len; i++) {
      const modelInfo = modelInfos[i];
      const entities = this._virtualEntityMap.get(modelInfo.id);
      if (entities && entities.length) {
        if (modelInfo.type === EEntityType.Furniture) {
          entities[0].update(modelInfo.data);
        } else if (modelInfo.type === EEntityType.Assembly) {
          if (entities.length === modelInfo.subModeldatas.length) {
            for (let j = 0; j < entities.length; j++) {
              entities[j].update(modelInfo.subModeldatas[j]);
            }
          }
        }
      } else {
        const furnitureEntities: FurnitureEntity[] = [];
        if (modelInfo.type === EEntityType.Furniture) {
          const furnitureEntity = new FurnitureEntity({ id: modelInfo.id + MULTI_ID_SUFFIX });
          furnitureEntity.update(modelInfo.data);
          furnitureEntities.push(furnitureEntity);
        } else if (modelInfo.type === EEntityType.Assembly) {
          for (const subModeldata of modelInfo.subModeldatas) {
            const furnitureEntity = new FurnitureEntity({ id: subModeldata.id + MULTI_ID_SUFFIX });
            furnitureEntity.update(subModeldata);
            furnitureEntities.push(furnitureEntity);
          }
        }

        this._virtualEntityMap.set(modelInfo.id, furnitureEntities);
      }
    }
  }

  private clearVirtualEntity() {
    for (const entities of this._virtualEntityMap.values()) {
      for (const entity of entities) {
        entity.dispose();
      }
    }
    this._virtualEntityMap.clear();
    this._rulerModel.clear();
  }

  private updateBoxes(params?: { x: number; y: number; rotation: number }) {
    if (!this._checkedData) return;

    this._selectedBoxes.update({ type: EEntityType.MultiModel, entityId: MULTI_SELECT_TEMPORARY_ID }, params);
  }

  clear() {
    this.clearVirtualEntity();
    this._selectedBoxes.clear();
    this._virtualEntityMap.clear();
    this._multiModelAbsorbManager.clearModel();
    this._rulerModel.clear();
    this._checkedData = null;
    this._virtualData = null;
  }

  override dispose() {
    this.clear();

    this._selectedBoxes.dispose();
    this._rulerModel.dispose();
    this._multiModelAbsorbManager.dispose();

    globalStores.editorStateStore.virtualMultiModelData = null;

    super.dispose();
  }
}
